import React, {useContext, useMemo, useState} from 'react';
import {Dialog, DialogBody, DialogFooter, Button, Text, Col, ModalComponentProps} from '@mc/ui-v4';
import {FeatureMultipleSelector, LayerId} from '@mc/react-mapio';
import './MultipleFeaturesSelection.scss';
import {WaitForQueryData} from '@mc/react-app-v4';
import {CatalogItemVO} from '../../api';
import {useCatalogLayers} from '../../hooks/useCatalogLayers';
import {UseQueryResult} from 'react-query';
import {MapioContext, MapioContextProp} from '../../mapioContext';

type Props = ModalComponentProps & {
    title: string;
    featurePointers?: FeatureMultipleSelector[];
    totalCount?: number;
    onClick: (feature: FeatureMultipleSelector) => void;
};

/** Počet položek vykreslených v DOM najednou */
const PAGE_SIZE = 40;

const estimateRowWidth = (featureLabel: string, layerLabel: string): number => {
    return (featureLabel.length + layerLabel.length) * 6;
};

/** Jednorázové lineární projití stromu katalogu → mapa layerId → CatalogItemVO */
const buildLayerMap = (layers: CatalogItemVO[] | undefined): Map<LayerId, CatalogItemVO> => {
    const map = new Map<LayerId, CatalogItemVO>();
    if (!layers) return map;
    const stack = [...layers];
    while (stack.length > 0) {
        const item = stack.pop();
        if (!item) continue;
        if (item.layerId != null) map.set(item.layerId, item);
        if (item.children?.length) stack.push(...item.children);
    }
    return map;
};

export const MultipleFeaturesSelection: React.FC<Props> = ({title, featurePointers, totalCount, onClose, onClick}) => {
    const mapioContext: MapioContextProp = useContext(MapioContext);
    const catalogLayersQuery: UseQueryResult<CatalogItemVO[]> = useCatalogLayers(mapioContext.value.topicId);

    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    // Mapa layerId → CatalogItemVO, přepočítá se pouze při změně katalogu
    const layerMap = useMemo(() => buildLayerMap(catalogLayersQuery.data), [catalogLayersQuery.data]);

    // Předpočítané záznamy – O(n) průchod místo opakovaných rekurzivních hledání
    const rows = useMemo(() => {
        if (!featurePointers) return [];
        return featurePointers
            .map(feature => {
                const layer = layerMap.get(feature.layerId);
                const layerLabel = layer?.label ?? '';
                const rawLabel = feature.label ?? feature.featureId?.toString() ?? 'Neznámý prvek';
                const label = typeof rawLabel === 'string' ? rawLabel.replace(`- ${layerLabel}`, '') : rawLabel;
                return {feature, label, layerLabel};
            })
            .sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, {numeric: true, sensitivity: 'base'}));
    }, [featurePointers, layerMap]);

    const dialogSize = useMemo(() => {
        if (!rows.length) return 'sm';
        let maxWidth = 0;
        for (const {label, layerLabel} of rows) {
            maxWidth = Math.max(maxWidth, estimateRowWidth(String(label), layerLabel));
        }
        if (maxWidth <= 300) return 'sm';
        if (maxWidth <= 500) return 'md';
        return 'lg';
    }, [rows]);

    const visibleRows = rows.slice(0, visibleCount);
    const hiddenInList = rows.length - visibleRows.length;
    const isTruncated = totalCount != null && totalCount > rows.length;

    return (
        <Dialog isOpen size={dialogSize} onClose={onClose} title={title}>
            <DialogBody>
                <WaitForQueryData queries={[catalogLayersQuery]}>
                    {() => (
                        <>
                            {visibleRows.map(({feature, label, layerLabel}) => (
                                <div className={'selection-rows'} key={`pointer-${feature.featureId}`}>
                                    <Col>
                                        <Button
                                            onClick={() => {
                                                onClick(feature);
                                                onClose();
                                            }}
                                            fill
                                            className={'justify-content-start'}
                                        >
                                            <Text intent={'default'}>{label || feature.featureId || feature.groupId || feature || 'Neznámý prvek'}</Text>{' '}
                                            <Text size={'normal'}>{layerLabel}</Text>
                                        </Button>
                                    </Col>
                                </div>
                            ))}
                            {hiddenInList > 0 && (
                                <div className={'selection-rows'}>
                                    <Col>
                                        <Button fill style="default" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
                                            <Text>
                                                Načíst další ({Math.min(PAGE_SIZE, hiddenInList)} z {hiddenInList} zbývajících)
                                            </Text>
                                        </Button>
                                    </Col>
                                </div>
                            )}
                            {isTruncated && (
                                <div className={'selection-rows selection-rows--hint'}>
                                    <Col>
                                        <Text size={'small'} intent={'secondary'}>
                                            Zobrazeno {rows.length} z {totalCount} objektů. Pro zobrazení všech objektů mapu přibližte.
                                        </Text>
                                    </Col>
                                </div>
                            )}
                        </>
                    )}
                </WaitForQueryData>
            </DialogBody>
            <DialogFooter>
                <Button style="primary" onClick={onClose}>
                    Zrušit
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
